import type { EditorValue } from '@octopus-do/octopus-editor'

// Slate fills empty leaves and void spacers with zero-width characters; they
// are not typed text. Whitespace is dropped on both sides, so the DOM and the
// value are compared by the characters the user actually typed.
const NOT_TYPED = /[﻿​\s]/g

// A line of copy: enough to rule out a stray character Slate renders and the
// value trims, and short enough to catch a single lost sentence.
export const TEXT_LOSS_THRESHOLD = 20

interface TextNode {
  text?: unknown
  children?: unknown
}

const countTyped = (text: string) => text.replace(NOT_TYPED, '').length

export const getModelTextLength = (nodes: unknown): number => {
  if (!Array.isArray(nodes)) {
    return 0
  }

  return nodes.reduce<number>((sum, node: TextNode) => {
    const own = typeof node.text === 'string' ? countTyped(node.text) : 0

    return sum + own + getModelTextLength(node.children)
  }, 0)
}

export const getDomTextLength = (editable: Element): number =>
  countTyped(editable.textContent ?? '')

// How many typed characters the editor shows that its value does not carry.
export const measureTextLoss = (
  editable: Element,
  value: EditorValue
): number => getDomTextLength(editable) - getModelTextLength(value)

// Writing assistants known to hijack contentEditable input. Grammarly marks
// the body when its extension loads and injects its own elements; LanguageTool
// marks the html element and injects a toolbar.
const EXTENSION_MARKERS: [string, string][] = [
  [
    'grammarly',
    'body[data-gr-ext-installed], body[data-new-gr-c-s-check-loaded], grammarly-extension, grammarly-desktop-integration, grammarly-popups',
  ],
  ['languagetool', 'html[data-lt-installed], lt-toolbar, lt-div, lt-mirror'],
]

export const detectEditorExtensions = (root: ParentNode = document): string =>
  EXTENSION_MARKERS.filter(([, selector]) => root.querySelector(selector))
    .map(([name]) => name)
    .join('+') || 'none'

// The rest of the page is described without a list of suspects: anything an
// extension leaves behind is named by tag, id, class or attribute name, never
// by content. Fits the `details` field of a client event.
export const DETAILS_MAX_LENGTH = 1000
const LIST_LIMIT = 6
const NAME_LIMIT = 24

// Our own roots in index.html; everything else under body came from elsewhere.
const OWN_BODY_CHILDREN = new Set([
  'root',
  'welcome',
  'overlay-modals',
  'overlay-notifications',
  'overlay-confirmations',
  'tooltipPortal',
])
const SILENT_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'NOSCRIPT', 'TEMPLATE'])

const EXTENSION_RESOURCES = [
  'chrome-extension:',
  'moz-extension:',
  'safari-web-extension:',
]
  .flatMap((scheme) => [`[src^="${scheme}"]`, `[href^="${scheme}"]`])
  .join(',')

// The events typed text goes through on its way into Slate. `beforeinput`
// is what Slate listens to: keys without it are exactly what gets lost.
export const INPUT_EVENTS = [
  'keydown',
  'beforeinput',
  'input',
  'compositionstart',
] as const

export type InputEventName = (typeof INPUT_EVENTS)[number]

export interface InputActivity {
  counts: Record<InputEventName, number>
  inputTypes: string[]
}

export const createInputActivity = (): InputActivity => ({
  counts: { keydown: 0, beforeinput: 0, input: 0, compositionstart: 0 },
  inputTypes: [],
})

export const countInputEvent = (
  activity: InputActivity,
  event: Event
): InputActivity => {
  const name = event.type as InputEventName
  const inputType = event instanceof InputEvent ? event.inputType : ''
  const inputTypes =
    inputType && !activity.inputTypes.includes(inputType)
      ? [...activity.inputTypes, inputType]
      : activity.inputTypes

  return {
    counts: { ...activity.counts, [name]: activity.counts[name] + 1 },
    inputTypes,
  }
}

const unique = (items: string[]) =>
  [...new Set(items)].slice(0, LIST_LIMIT).join(',')

const describeElement = (el: Element): string => {
  const id = el.id ? `#${el.id.slice(0, NAME_LIMIT)}` : ''
  const cls = el.classList[0] ? `.${el.classList[0].slice(0, NAME_LIMIT)}` : ''
  const shadow = el.shadowRoot ? '{shadow}' : ''

  return `${el.tagName.toLowerCase()}${id}${cls}${shadow}`
}

const dataAttributes = (el: Element | null): string =>
  unique(
    (el?.getAttributeNames() ?? []).filter((name) => name.startsWith('data-'))
  )

const isForeignChild = (el: Element) =>
  !OWN_BODY_CHILDREN.has(el.id) && !SILENT_TAGS.has(el.tagName)

const isCustomElement = (el: Element) => el.tagName.includes('-')

export const describePage = (root: Document = document): string[] => {
  const extensionResources = root.querySelectorAll(EXTENSION_RESOURCES).length
  const customElements = unique(
    [...root.querySelectorAll('*')].filter(isCustomElement).map(describeElement)
  )
  const strangers = unique(
    [...root.body.children].filter(isForeignChild).map(describeElement)
  )
  const html = dataAttributes(root.documentElement)
  const body = dataAttributes(root.body)

  return [
    extensionResources > 0 && `ext=${extensionResources}`,
    html && `html=${html}`,
    body && `body=${body}`,
    customElements && `custom=${customElements}`,
    strangers && `body>=${strangers}`,
  ].filter((part): part is string => Boolean(part))
}

// Elements inside the editable that Slate did not render: an extension that
// draws into the editor shows up here by tag.
export const describeEditable = (editable: Element): string[] => {
  const foreign = unique(
    [...editable.querySelectorAll('*')]
      .filter(
        (el) =>
          isCustomElement(el) ||
          el.shadowRoot ||
          el.matches(EXTENSION_RESOURCES)
      )
      .map(describeElement)
  )

  return foreign ? [`editor=${foreign}`] : []
}

export const describeInputActivity = ({
  counts,
  inputTypes,
}: InputActivity): string[] => [
  `events=${INPUT_EVENTS.map((name) => `${name}:${counts[name]}`).join(',')}`,
  ...(inputTypes.length > 0 ? [`input=${unique(inputTypes)}`] : []),
]

// A snapshot is a side note to the save path it describes and must never
// break it: a DOM that refuses to be walked is reported as such.
export const describeTextLoss = (
  editable: Element,
  activity: InputActivity,
  root: Document = document
): string => {
  try {
    return [
      ...describePage(root),
      ...describeEditable(editable),
      ...describeInputActivity(activity),
    ]
      .join(';')
      .slice(0, DETAILS_MAX_LENGTH)
  } catch {
    return 'unavailable'
  }
}
